import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, Divider, Grid, LinearProgress, Skeleton, Typography } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { fetchCreatorAnalytics, CreatorAnalytics } from '../graphqlService';
import { brand } from '../theme';
import { formatBytes, pluralize } from '../utils/format';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import StatCard from './ui/StatCard';
import EmptyState from './ui/EmptyState';
import KeyValueList from './ui/KeyValueList';
import StatusBreakdown from './Insights/StatusBreakdown';
import ProjectsByMonthChart from './Insights/ProjectsByMonthChart';
import { buildStatusRows, formatPercent } from './Insights/insightsModel';

const LOAD_ERROR = "We couldn't load your insights. Check your connection and try again.";

/** Skeletons in the shape of the page: four stat tiles and two panels. */
const InsightsSkeleton: React.FC = () => (
  <Box role="status" aria-live="polite" aria-label="Loading insights">
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      {[0, 1, 2, 3].map((i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Card sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="rounded" width={44} height={44} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="40%" height={32} />
              <Skeleton width="70%" />
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Card sx={{ p: 2.5 }}>
          <Skeleton width="40%" sx={{ mb: 2 }} />
          {[0, 1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton width={`${50 + i * 10}%`} />
              <Skeleton variant="rounded" height={8} />
            </Box>
          ))}
        </Card>
      </Grid>
      <Grid item xs={12} md={5}>
        <Card sx={{ p: 2.5 }}>
          <Skeleton width="35%" sx={{ mb: 2 }} />
          <Skeleton width="30%" height={48} />
          <Skeleton variant="rounded" height={6} sx={{ my: 2 }} />
          <Skeleton width="80%" />
          <Skeleton width="60%" />
        </Card>
      </Grid>
    </Grid>
  </Box>
);

const Insights: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<CreatorAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadFailed(false);
      // fetchCreatorAnalytics resolves to null on any failure.
      const result = await fetchCreatorAnalytics();
      if (cancelled) return;
      setData(result);
      setLoadFailed(!result);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const statusRows = useMemo(() => buildStatusRows(data?.projectsByStatus ?? []), [data]);
  const months = data?.projectsByMonth ?? [];
  const noRecentProjects = months.length > 0 && months.every((m) => !(Number(m.count) > 0));

  const header = <PageHeader title="Insights" subtitle="How your projects, revisions and editors add up over time." />;

  const renderBody = () => {
    if (loading) return <InsightsSkeleton />;

    if (loadFailed || !data) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          {LOAD_ERROR}
        </Alert>
      );
    }

    if (data.totalProjects === 0) {
      return (
        <Card>
          <EmptyState
            icon={<BarChartOutlinedIcon />}
            title="No insights yet"
            description="Once you send your first project to an editor, its progress, revisions and timeline show up here."
            actionLabel="New project"
            onAction={() => navigate('/submit-editing-request')}
            secondaryActionLabel="Getting started"
            onSecondaryAction={() => navigate('/how-to')}
          />
        </Card>
      );
    }

    const completionRate = Math.min(100, Math.max(0, Number(data.completionRate) || 0));

    return (
      <>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<FolderOutlinedIcon />} label="Projects" value={data.totalProjects} to="/my-projects" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<TimelapseOutlinedIcon />} label="Active" value={data.activeProjects} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TaskAltOutlinedIcon />}
              label="Completed"
              value={data.completedProjects}
              to="/my-projects?status=done"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<GroupOutlinedIcon />} label="Active editors" value={data.activeEditors} to="/my-editors" />
          </Grid>
        </Grid>

        <Grid container spacing={3} alignItems="stretch" sx={{ mb: 3 }}>
          <Grid item xs={12} md={7}>
            <Section
              title="Projects by status"
              description="Where each of your projects stands today."
              sx={{ height: '100%' }}
            >
              {statusRows.length > 0 ? (
                <StatusBreakdown rows={statusRows} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No projects yet.
                </Typography>
              )}
            </Section>
          </Grid>
          <Grid item xs={12} md={5}>
            <Section
              title="Completion"
              description="Projects approved or delivered, out of all your projects."
              sx={{ height: '100%' }}
            >
              <Typography
                variant="h3"
                component="p"
                sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.primary', lineHeight: 1.1 }}
              >
                {formatPercent(completionRate)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                {`${data.completedProjects} of ${pluralize(data.totalProjects, 'project')}`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completionRate}
                aria-label={`Completion rate: ${formatPercent(completionRate)}`}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: brand.slate },
                }}
              />
              <Divider sx={{ my: 2.5 }} />
              <KeyValueList
                items={[
                  { label: 'Revisions received', value: data.totalRevisions },
                  { label: 'Revisions per project', value: data.avgRevisionsPerProject },
                  { label: 'Storage used', value: formatBytes(data.storageUsedBytes) },
                  { label: 'Editors invited', value: data.totalEditors },
                ]}
              />
            </Section>
          </Grid>
        </Grid>

        <Section
          title="Projects created"
          description={noRecentProjects ? 'No new projects in the last 6 months.' : 'The last 6 months, by the month each project was created.'}
        >
          <ProjectsByMonthChart months={months} />
        </Section>
      </>
    );
  };

  return (
    <PageContainer>
      {header}
      {renderBody()}
    </PageContainer>
  );
};

export default Insights;
